import React, { useState, useRef } from "react";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { toast } from 'react-toastify';

export const CropShape = ({
    onChangeCropper,
    maxSizeMib = 2,
    height = 200,
    width = 200,
    guid,
    imagePreview,
    buttonBackGroundColor = "#ccc",
    cropWidth,
    cropHeight,
    aspectRatio
}) => {
    const [modalIsOpen, setIsOpen] = useState(false);
    const inputRef = useRef(null);
    const [image, setImage] = useState("");
    const [cropper, setCropper] = useState('');
    const fileRef = useRef()

    const onChange = (e) => {
        e.preventDefault();
        let files;
        if (e.dataTransfer) {
            files = e.dataTransfer.files;
        } else if (e.target) {
            files = e.target.files;
        }
        if (files && files.length) {
            const fileSizeMB = files[0].size / (1024 * 1024);
            if (fileSizeMB > maxSizeMib) {
                toast.error(`File Must Be Less Than ${maxSizeMib} MB`, { autoClose: 3000 });
                return;
            }
            const reader = new FileReader();
            reader.onload = () => {
                setImage(reader.result);
                setIsOpen(true)
            };
            reader.readAsDataURL(files[0]);
            fileRef.current = files[0];
        }
    };

    const b64toBlob = async (dataURI) => {
        const base64file = await fetch(dataURI)
        const blobFile = await base64file.blob()
        return blobFile
    }

    const getCropData = async () => {
        if (typeof cropper !== 'undefined') {
            // Get cropped canvas with exact dimensions
            let cropperCanvas = cropper.getCroppedCanvas({
                width: cropWidth,
                height: cropHeight,
                imageSmoothingEnabled: true,
                imageSmoothingQuality: 'high'
            });
            
            const fileCropped = await b64toBlob(cropperCanvas.toDataURL('image/png', 1.0))
            onChangeCropper({
                fileCropped,
                originalFile: fileRef.current,
                fileCroppedBase64: cropperCanvas.toDataURL('image/png', 1.0),
            })
            setIsOpen(false)
        }
    }

    const triggerInvokedFromParent = () => {
        inputRef?.current?.click();
    };

    // Calculate aspect ratio from width and height
    const calculatedAspectRatio = aspectRatio || (cropWidth && cropHeight ? cropWidth / cropHeight : null);

    return (
        <div>
            <div 
                style={{ 
                    display: "block", 
                    width: width, 
                    height: height, 
                    background: buttonBackGroundColor, 
                    position: "relative",
                    cursor: "pointer",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    overflow: "hidden"
                }} 
                onClick={triggerInvokedFromParent}
            >
                <input 
                    style={{ height: "100%", width: "100%", visibility: "hidden", position: "absolute" }} 
                    ref={inputRef} 
                    type="file" 
                    name={`fileUploadCropShape_${guid || 'default'}`} 
                    id={`fileUploadCropShape_${guid || 'default'}`} 
                    accept="image/*"
                    onChange={onChange} 
                />
                {imagePreview ? (
                    <img 
                        src={imagePreview} 
                        style={{ 
                            width: '100%', 
                            height: "100%", 
                            position: 'absolute', 
                            left: 0, 
                            top: 0,
                            objectFit: 'contain'
                        }} 
                        alt="Preview" 
                    />
                ) : (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: '#666',
                        fontSize: '14px'
                    }}>
                        Click to upload
                    </div>
                )}
            </div>
            <Modal size="lg" backdrop="static" keyboard={false} show={modalIsOpen} onHide={() => setIsOpen(false)}>
                <Modal.Header>
                    <Modal.Title>Crop Image ({cropWidth} x {cropHeight})</Modal.Title>
                    <Button
                        variant="light"
                        onClick={() => setIsOpen(false)}
                        style={{ border: 'none', boxShadow: 'none' }}
                        className="simple-icon-close text-danger"
                    >
                        ×
                    </Button>
                </Modal.Header>
                <Modal.Body>
                    {calculatedAspectRatio && (
                        <div className="mb-2">
                            <small className="text-muted">
                                Aspect Ratio: {calculatedAspectRatio.toFixed(2)} ({cropWidth} x {cropHeight})
                            </small>
                        </div>
                    )}
                    <Cropper
                        aspectRatio={calculatedAspectRatio}
                        src={image}
                        zoomable={true}
                        minCropBoxHeight={cropHeight || 10}
                        minCropBoxWidth={cropWidth || 10}
                        background={false}
                        responsive={true}
                        cropBoxResizable={true}
                        cropBoxMovable={true}
                        checkOrientation={false}
                        viewMode={1} // Restrict crop box within canvas
                        dragMode="move"
                        autoCropArea={0.8}
                        restore={false}
                        guides={true}
                        center={true}
                        highlight={true}
                        onInitialized={(instance) => {
                            setCropper(instance);
                            // Set initial crop box size
                            if (cropWidth && cropHeight) {
                                setTimeout(() => {
                                    const containerData = instance.getContainerData();
                                    const imageData = instance.getImageData();
                                    
                                    // Calculate initial crop box size (80% of image or exact size, whichever is smaller)
                                    const maxWidth = Math.min(imageData.width * 0.8, cropWidth);
                                    const maxHeight = Math.min(imageData.height * 0.8, cropHeight);
                                    
                                    // Set crop box to maintain aspect ratio
                                    const scale = Math.min(
                                        maxWidth / cropWidth,
                                        maxHeight / cropHeight
                                    );
                                    
                                    instance.setCropBoxData({
                                        left: (containerData.width - cropWidth * scale) / 2,
                                        top: (containerData.height - cropHeight * scale) / 2,
                                        width: cropWidth * scale,
                                        height: cropHeight * scale
                                    });
                                }, 100);
                            }
                        }}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setIsOpen(false)}>Close</Button>
                    <Button variant="primary" onClick={getCropData}>Crop</Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

export default CropShape;

